import type { MutableRefObject } from 'react';

import { roundCurrency2 } from './payment-math';

export type SplitAuthoritativeField = '1' | '2';

export interface SyncSplitAmountsParams {
  total: number;
  editedField: SplitAuthoritativeField;
  rawValue: string;
  setFieldsValue: (patch: Record<string, string>) => void;
  lastEditedRef: MutableRefObject<SplitAuthoritativeField>;
}

/**
 * Keeps C1 + C2 = total (2 d.p.). The edited field is authoritative; the other is derived.
 * Does not write negative or > total into the derived field — caller should validate for errors.
 */
export function syncSplitAmountsFromEditedField({
  total,
  editedField,
  rawValue,
  setFieldsValue,
  lastEditedRef,
}: SyncSplitAmountsParams): void {
  lastEditedRef.current = editedField;
  const parsed = Number.parseFloat(rawValue.trim());
  if (Number.isNaN(parsed)) {
    return;
  }
  const clamped = roundCurrency2(Math.min(Math.max(parsed, 0), total));
  const other = roundCurrency2(total - clamped);
  if (editedField === '1') {
    setFieldsValue({
      splitAmount1: clamped.toFixed(2),
      splitAmount2: other.toFixed(2),
    });
  } else {
    setFieldsValue({
      splitAmount2: clamped.toFixed(2),
      splitAmount1: other.toFixed(2),
    });
  }
}

/** Updates only the non-edited field so the active input keeps the user's keystrokes. */
export function syncPeerSplitAmountOnly({
  total,
  editedField,
  rawValue,
  setFieldsValue,
  lastEditedRef,
}: SyncSplitAmountsParams): void {
  lastEditedRef.current = editedField;
  const parsed = Number.parseFloat(rawValue.trim());
  if (Number.isNaN(parsed)) {
    return;
  }
  const clamped = roundCurrency2(Math.min(Math.max(parsed, 0), total));
  const other = roundCurrency2(total - clamped);
  if (editedField === '1') {
    setFieldsValue({ splitAmount2: other.toFixed(2) });
  } else {
    setFieldsValue({ splitAmount1: other.toFixed(2) });
  }
}

export function initializeSplitAmountsEqual(total: number): Record<string, string> {
  const half = roundCurrency2(total / 2);
  const rest = roundCurrency2(total - half);
  return {
    splitAmount1: half.toFixed(2),
    splitAmount2: rest.toFixed(2),
  };
}

export function initializeSplitDownEqual(downTotal: number): Record<string, string> {
  const half = roundCurrency2(downTotal / 2);
  const rest = roundCurrency2(downTotal - half);
  return {
    splitDownAmount1: half.toFixed(2),
    splitDownAmount2: rest.toFixed(2),
  };
}

export function syncDownSplitAmountsFromEditedField({
  downTotal,
  editedField,
  rawValue,
  setFieldsValue,
  lastEditedRef,
}: Omit<SyncSplitAmountsParams, 'total'> & { downTotal: number }): void {
  lastEditedRef.current = editedField;
  const parsed = Number.parseFloat(rawValue.trim());
  if (Number.isNaN(parsed)) {
    return;
  }
  const clamped = roundCurrency2(Math.min(Math.max(parsed, 0), downTotal));
  const other = roundCurrency2(downTotal - clamped);
  if (editedField === '1') {
    setFieldsValue({
      splitDownAmount1: clamped.toFixed(2),
      splitDownAmount2: other.toFixed(2),
    });
  } else {
    setFieldsValue({
      splitDownAmount2: clamped.toFixed(2),
      splitDownAmount1: other.toFixed(2),
    });
  }
}

export function syncPeerDownSplitAmountOnly({
  downTotal,
  editedField,
  rawValue,
  setFieldsValue,
  lastEditedRef,
}: Omit<SyncSplitAmountsParams, 'total'> & { downTotal: number }): void {
  lastEditedRef.current = editedField;
  const parsed = Number.parseFloat(rawValue.trim());
  if (Number.isNaN(parsed)) {
    return;
  }
  const clamped = roundCurrency2(Math.min(Math.max(parsed, 0), downTotal));
  const other = roundCurrency2(downTotal - clamped);
  if (editedField === '1') {
    setFieldsValue({ splitDownAmount2: other.toFixed(2) });
  } else {
    setFieldsValue({ splitDownAmount1: other.toFixed(2) });
  }
}
